import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { RefreshCw, Save, Timer } from "lucide-react";

type WorkTimeRulesSettingsForm = {
  work_time_max_shift_hours: string;
  work_time_min_rest_hours: string;
  work_time_max_cumulative_hours: string;
  work_time_reset_break_hours: string;
};

const DEFAULTS: WorkTimeRulesSettingsForm = {
  work_time_max_shift_hours: "14",
  work_time_min_rest_hours: "10",
  work_time_max_cumulative_hours: "70",
  work_time_reset_break_hours: "24",
};

const SETTING_NAMES = Object.keys(DEFAULTS) as (keyof WorkTimeRulesSettingsForm)[];

const WorkTimeRulesSettings: React.FC = () => {
  const [form, setForm] = useState<WorkTimeRulesSettingsForm>(DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("settings").select("name, value").in("name", SETTING_NAMES);
    setLoading(false);

    if (error) {
      toast({ title: "Work Time Rules error", description: error.message, variant: "destructive" });
      return;
    }

    const mapped = { ...DEFAULTS };
    (data || []).forEach((row) => {
      if (row.name in mapped) mapped[row.name as keyof WorkTimeRulesSettingsForm] = row.value || "";
    });
    setForm(mapped);
  };

  useEffect(() => {
    load();
  }, []);

  const updateField = (field: keyof WorkTimeRulesSettingsForm, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const save = async () => {
    setSaving(true);

    const rows = [
      {
        name: "work_time_max_shift_hours",
        value: form.work_time_max_shift_hours,
        category: "WorkTime",
        label: "Maximum Shift Length (hours)",
        description: "The longest a single shift can span, start to end. NZ default: 14 hours (allowing for 2x 30 min breaks, leaving 13 working hours).",
        type: "number",
        sort_order: 10,
        is_active: true,
      },
      {
        name: "work_time_min_rest_hours",
        value: form.work_time_min_rest_hours,
        category: "WorkTime",
        label: "Minimum Rest Between Shifts (hours)",
        description: "The shortest gap allowed between one shift ending and the next one starting for the same driver.",
        type: "number",
        sort_order: 20,
        is_active: true,
      },
      {
        name: "work_time_max_cumulative_hours",
        value: form.work_time_max_cumulative_hours,
        category: "WorkTime",
        label: "Maximum Cumulative Work Hours",
        description: "The most a driver can work before a reset break is required. NZ default: 70 hours (e.g. 7x10 hour days).",
        type: "number",
        sort_order: 30,
        is_active: true,
      },
      {
        name: "work_time_reset_break_hours",
        value: form.work_time_reset_break_hours,
        category: "WorkTime",
        label: "Reset Break Length (hours)",
        description: "A break of at least this length resets the cumulative work hours count back to zero.",
        type: "number",
        sort_order: 40,
        is_active: true,
      },
    ];

    const { error } = await supabase.from("settings").upsert(rows, { onConflict: "name" });
    setSaving(false);

    if (error) {
      toast({ title: "Not saved", description: error.message, variant: "destructive" });
      return;
    }

    toast({ title: "Saved", description: "Work Time Rules updated." });
  };

  const renderNumberSetting = (field: keyof WorkTimeRulesSettingsForm, label: string, description: string, suffix: string) => (
    <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
      <div>
        <Label className="text-foreground">{label}</Label>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={1}
          value={form[field]}
          onChange={(e) => updateField(field, e.target.value)}
          className="dark-input"
        />
        <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Timer className="h-5 w-5 text-yellow-500" />
            Work Time Rules
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Driver work-time compliance rules -- these differ by country and company, so they're fully configurable here rather than hardcoded.
            The roster blocks any shift that would breach them, and flags any existing shift that already does.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {renderNumberSetting(
          "work_time_max_shift_hours",
          "Maximum Shift Length",
          "Longest a single shift can span from start to end.",
          "hours"
        )}
        {renderNumberSetting(
          "work_time_min_rest_hours",
          "Minimum Rest Between Shifts",
          "Shortest gap allowed between one shift ending and the next starting.",
          "hours"
        )}
        {renderNumberSetting(
          "work_time_max_cumulative_hours",
          "Maximum Cumulative Work Hours",
          "Most a driver can work before a reset break is required.",
          "hours"
        )}
        {renderNumberSetting(
          "work_time_reset_break_hours",
          "Reset Break Length",
          "A break at least this long resets the cumulative hours count to zero.",
          "hours"
        )}
      </div>

      <Button onClick={save} disabled={saving} className="w-full golden-button">
        <Save className="w-4 h-4 mr-2" />
        {saving ? "Saving Work Time Rules..." : "Save Work Time Rules"}
      </Button>
    </div>
  );
};

export default WorkTimeRulesSettings;
